import { buildRequest, requestV2, requestV2Internal } from './request'
import { findRegionByLocale } from '@/common/region-locale'
import qs from 'qs'
import type { MediaAlt } from '../common/media-alt'

type RobotMeta = {
  id: number
  region: string
  title: string
  desc: string
  mediaUrl: string
  alts: MediaAlt[] | null
  jumpUrl: string
  code: string
  type: string
  tags: string[]
  iconTags: string[]
  created: number
  updated: number
  status: number
  sort: number
  operatorId: string
  categoryId: number
  versionName: string
  price: string
  otherJson: string
  tagsWithUrl?: { tag: string; url?: string }[]
}

type NavbarMenu = {
  title: string
  item: Array<{
    item: Array<RobotMeta>
  }>
}

type BottomMenu = {
  id: number
  title: string
  desc: string
  code: string
  type: string
  jumpUrl: string
  mediaUrl: string
  item: Array<{
    id: number
    title: string
    desc: string
    code: string
    type: string
    jumpUrl: string
    mediaUrl: string
    item: string[]
    productItem: string[]
  }>
  productItem: string[]
}

type SocialLink = {
  jumpUrl: string // link url
  mediaUrl: string // icon url
  isJump: boolean
  title: string
}

type FriendLink = {
  title: string
  jumpUrl: string
}

type SolutionNav = {
  title: string
  jumpUrl: string
}

type EventNav = {
  title: string
  jumpUrl: string
}

export type CommonData = {
  topNavProducts: Array<NavbarMenu>
  bottomNav: Array<BottomMenu>
  eventNav: Array<EventNav>
  threeJump: Array<SocialLink>
  friendLink: Array<FriendLink>
  solutionNav: Array<SolutionNav>
  productBannerMediaUrl: string
  ProductBannerMediaUrlH5: string
}

const request = buildRequest('/api/front/v1')

export async function getPageCommonData(locale: string, isInternal: boolean = false): Promise<CommonData> {
  const params: any = {}
  if (locale) {
    params.region = findRegionByLocale(locale)
  }
  const res = isInternal ? await requestV2Internal('/common', params) : await requestV2('/common', params)
  return res
}

export function buildSWRKey(locale: string): string {
  return `/${locale}/api/front/v2/common`
}

export type SearchCategoryItem = {
  title: string
  count: number
  infoItems: { content: string; jumpUrl: string }[]
}

export type SearchResultData = {
  categoryItems: SearchCategoryItem[]
  totalCount: number
  quickLinks: { content: string; jumpUrl: string }[]
}

export function search(searchKey: string): Promise<SearchResultData> {
  const res = request('/search/info', { search_key: searchKey })
  return res
}

/**
 * Strapi navigation 内容类型中 new_three_jump 组件的数据结构
 * @description 对应 Strapi 中 new_three_jump 可重复组件，包含社交媒体图标及跳转信息
 */
export interface NewThreeJumpItem {
  id: number
  title: string
  isJump: boolean
  jumpUrl: string
  mediaUrl: { id: number; documentId: string; url: string; alts: MediaAlt[] | null } | null
  activeMediaUrl: { id: number; documentId: string; url: string; alts: MediaAlt[] | null } | null
  hoverMediaUrl: { id: number; documentId: string; url: string; alts: MediaAlt[] | null } | null
}

/** Strapi navigation API 响应中与 new_three_jump 相关的数据结构 */
interface NavigationThreeJumpResponse {
  data: {
    id: number
    documentId: string
    new_three_jump: NewThreeJumpItem[]
  }
  meta: Record<string, any>
}

/** Strapi populate 查询参数，填充 new_three_jump 组件的三个媒体字段 */
const navigationThreeJumpQuery = qs.stringify(
  {
    populate: {
      new_three_jump: {
        populate: {
          mediaUrl: { fields: ['url', 'alts'] },
          activeMediaUrl: { fields: ['url', 'alts'] },
          hoverMediaUrl: { fields: ['url', 'alts'] },
        },
      },
    },
  },
  { encodeValuesOnly: true }
)

/**
 * 通过 /common/data 透传接口获取 navigation 中的 new_three_jump 社交图标数据
 * @param locale - 语言/区域代码
 * @returns new_three_jump 数组
 */
export async function getNavigationThreeJump(locale: string): Promise<NewThreeJumpItem[]> {
  const region = findRegionByLocale(locale)
  const res: NavigationThreeJumpResponse = await requestV2('/common/data', {
    url: `/api/navigation?status=published&${navigationThreeJumpQuery}`,
    region,
  })
  return res?.data?.new_three_jump ?? []
}

/** SWR 缓存键 */
getNavigationThreeJump.swrkey = (locale: string) => `/${locale}/api/front/v2/common/data/navigation/new-three-jump`
